import { Combobox } from '@Components/Combobox';
import { useUsers } from '@Hooks/useUsers';
import { Table } from '@tanstack/react-table';
import { User } from '@Types/user';
import { useState, useEffect } from 'react';
import { useUserRoles } from '@Hooks/useUserRoles';

type UserFilterValue =
  | {
      operator?: string;
      value?: string;
    }
  | undefined;

interface UserQuickFilterProps<TData extends object> {
  table: Table<TData>;
}

export default function UserQuickFilter<TData extends object>({
  table,
}: UserQuickFilterProps<TData>) {
  const [userRoles, setUserRoles] = useState([]);
  const { isLoading } = useUsers<User[]>(
    { enabled: true },
    {
      select: (response) => response.data,
    },
  );

  const { data: payload } = useUserRoles({
    searchParams: {},
  });

  const userRoleColumn = table.getColumn('userRoles');

  const userRoleItems = [
    { value: '', label: 'All roles' },
    ...(userRoles ?? [])
      .map((role) => ({
        value: role.name,
        label: role.name,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      ),
  ];

  const currentFilter = (userRoleColumn?.getFilterValue() ??
    undefined) as UserFilterValue;

  const handleChange = (value?: string) => {
    if (!userRoleColumn) return;

    if (!value) {
      userRoleColumn.setFilterValue(undefined);
      return;
    }

    userRoleColumn.setFilterValue({
      operator: 'equals',
      value,
    });
  };

  useEffect(() => {
    if (payload) {
      setUserRoles(payload.data);
    }
  }, [payload]);

  return (
    <Combobox
      id="userFilter"
      items={userRoleItems}
      value={currentFilter?.value ?? ''}
      onChange={handleChange}
      placeholder="All roles"
      disabled={!userRoles?.length}
      loading={isLoading}
      triggerClassName="w-[300px]"
    />
  );
}
